import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiShield, FiDollarSign, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { cpApi } from '../api/api';
import { salaryBalanceBlocksTeamDeletion } from '../utils/salaryHoldDisplay';
import { formatCurrency } from '../utils/formatNumber';
import EditTeamMemberWidget from '../components/EditTeamMemberWidget';
import AddTeamMemberWidget from '../components/AddTeamMemberWidget';
import DeleteConfirmationWidget from '../components/DeleteConfirmationWidget';
import type { CounterpartyTeam, CounterpartyTeamMember } from '../types';

export default function TeamPage() {
  const navigate = useNavigate();
  const [team, setTeam] = useState<CounterpartyTeam | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [filteredMembers, setFilteredMembers] = useState<CounterpartyTeamMember[]>([]);
  const [editingMember, setEditingMember] = useState<CounterpartyTeamMember | null>(null);
  const [deletingMember, setDeletingMember] = useState<CounterpartyTeamMember | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);

  const loadTeam = () => {
    cpApi.getMyTeam().then((t) => {
      setTeam(t);
      setFilteredMembers(t?.members ?? []);
    });
  };

  useEffect(() => {
    Promise.all([cpApi.getMyTeam(), cpApi.getProfile()]).then(([t, p]) => {
      setTeam(t);
      setFilteredMembers(t?.members ?? []);
      setCurrentUserId(p?.id ?? null);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const h = () => setShowAddMember(true);
    window.addEventListener('openAddTeamMemberWidget', h);
    return () => window.removeEventListener('openAddTeamMemberWidget', h);
  }, []);

  useEffect(() => {
    const h = (e: Event) => {
      const q = ((e as CustomEvent<{ search?: string }>).detail?.search ?? '').toLowerCase().trim();
      if (!team?.members) return;
      if (!q) {
        setFilteredMembers(team.members);
        return;
      }
      const filtered = team.members.filter(
        (m) => (m.counterparty_name ?? '').toLowerCase().includes(q)
      );
      setFilteredMembers(filtered);
    };
    window.addEventListener('teamSearchChange', h);
    return () => window.removeEventListener('teamSearchChange', h);
  }, [team?.members]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        Вы не входите в команду
      </div>
    );
  }

  const canEdit = currentUserId != null && team != null && currentUserId === team.responsible_counterparty_id;
  if (!canEdit) {
    navigate('/requests', { replace: true });
    return null;
  }

  const members = filteredMembers;

  const getCardColor = (m: CounterpartyTeamMember) =>
    m.is_responsible
      ? 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-200 dark:from-blue-950/60 dark:via-indigo-950/60 dark:to-blue-950/60 dark:border-blue-800/50'
      : 'bg-gradient-to-br from-gray-50 to-slate-100 border-gray-200 dark:from-gray-900 dark:to-gray-800 dark:border-gray-700/60';

  const balanceZero = (m: CounterpartyTeamMember) =>
    !salaryBalanceBlocksTeamDeletion(m.salary_balance ?? 0);

  const canDeleteMember = (m: CounterpartyTeamMember) =>
    canEdit && !m.is_responsible && balanceZero(m);

  const handleConfirmDelete = async () => {
    if (!deletingMember) return;
    try {
      await cpApi.removeMyTeamMember(deletingMember.counterparty_id);
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Участник исключён из команды', type: 'success' } }));
      setDeletingMember(null);
      loadTeam();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      const msg = e?.response?.data?.error ?? 'Ошибка';
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: msg, type: 'error' } }));
    }
  };

  const getRoleBadge = (m: CounterpartyTeamMember) =>
    m.is_responsible ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
        <FiShield className="w-3 h-3 mr-1" />
        Ответственный
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <FiUser className="w-3 h-3 mr-1" />
        Участник
      </span>
    );

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {members.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiUser className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">Нет участников</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
            {team.members?.length === 0 ? 'В команде пока нет участников' : 'Ничего не найдено по запросу'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 md:gap-6">
          {members.map((m) => (
            <div
              key={m.id}
              className={`rounded-xl shadow-sm border p-4 sm:p-5 hover:shadow-md transition-shadow duration-200 ${getCardColor(m)}`}
            >
              <div className="mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {m.counterparty_name || `ID ${m.counterparty_id}`}
                </h3>
                <div className="flex items-center justify-between gap-2">
                  {getRoleBadge(m)}
                  {canEdit && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingMember(m); }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 dark:text-gray-300 dark:hover:text-blue-300 dark:hover:bg-white/5"
                        title="Редактировать"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canDeleteMember(m)) setDeletingMember(m);
                        }}
                        disabled={!canDeleteMember(m)}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          canDeleteMember(m)
                            ? 'text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-300 dark:hover:bg-red-950/30'
                            : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        }`}
                        title={!balanceZero(m) ? 'Нельзя исключить при ненулевом балансе' : m.is_responsible ? 'Нельзя исключить ответственного' : 'Исключить из команды'}
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-2 pt-2 border-t border-gray-200/60 dark:border-gray-700/60">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Баланс</span>
                  <span className="inline-flex items-center font-semibold text-green-600 dark:text-green-400">
                    <FiDollarSign className="w-3 h-3 mr-0.5" />
                    {formatCurrency(m.salary_balance ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <EditTeamMemberWidget
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        onSuccess={() => {
          setEditingMember(null);
          loadTeam();
        }}
        member={editingMember}
      />

      <DeleteConfirmationWidget
        isOpen={!!deletingMember}
        onClose={() => setDeletingMember(null)}
        onConfirm={handleConfirmDelete}
        title="Исключить участника"
        message={deletingMember ? `Исключить ${deletingMember.counterparty_name || `ID ${deletingMember.counterparty_id}`} из команды?` : ''}
        itemName={deletingMember?.counterparty_name}
        confirmText="Исключить"
      />

      {team && (
        <AddTeamMemberWidget
          teamName={team.name}
          isOpen={showAddMember}
          onClose={() => setShowAddMember(false)}
          onSuccess={() => {
            setShowAddMember(false);
            loadTeam();
          }}
        />
      )}
    </div>
  );
}
